import { Component, ChangeDetectionStrategy, effect, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { DeepLink } from './core/services/deep-link';
import { Invitation } from './core/services/invitation';
import { Notification as NotificationService } from './core/services/notification';
import { NetworkService } from './core/services/network';
import { OutboxService } from './core/services/outbox';
import { KeyboardService } from './core/services/keyboard';
import { OtaUpdateService } from './core/services/ota-update';
import { ToastService } from './core/services/toast';
import { Auth } from './core/services/auth';
import { ThemeService } from './core/white-label/theme.service';
import { ToastContainer } from './shared/components/toast-container/toast-container';
import { BottomNav } from './shared/components/bottom-nav/bottom-nav';
import { OfflineBanner } from './shared/components/offline-banner/offline-banner';
import { KeyboardToolbar } from './shared/components/keyboard-toolbar/keyboard-toolbar';
import { OtaUpdateSheet } from './shared/components/ota-update-sheet/ota-update-sheet';
import { markStartup, StartupMark, reportFirestore } from './core/startup-metrics';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    ToastContainer,
    OfflineBanner,
    KeyboardToolbar,
    OtaUpdateSheet,
    BottomNav,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-offline-banner />
    <app-toast-container />
    <router-outlet />
    <app-bottom-nav />
    <app-keyboard-toolbar />
    <app-ota-update-sheet />
  `,
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private deepLinkService = inject(DeepLink);
  private invitation = inject(Invitation);
  private notificationService = inject(NotificationService);
  private networkService = inject(NetworkService);
  private outbox = inject(OutboxService);
  private keyboardService = inject(KeyboardService);
  private otaUpdateService = inject(OtaUpdateService);
  private router = inject(Router);
  private toast = inject(ToastService);
  private auth = inject(Auth);
  private themeService = inject(ThemeService);

  /** Session pour laquelle l'autorisation a déjà été demandée. */
  private pushRequestedFor: string | null = null;

  /** Session pour laquelle l'invitation en attente a déjà été rouverte. */
  private invitationResumedFor: string | null = null;

  /**
   * Session qui s'ouvre : une invitation encore en attente reparaît.
   *
   * S'inscrire depuis une invitation traverse quatre écrans et une sortie vers
   * les SMS ; le chemin de retour peut s'y perdre — `authGuard` le jette quand
   * il renvoie vers `/auth`. Attendre le prochain démarrage laisserait alors la
   * personne sur l'accueil, sans trace de ce pour quoi elle a installé
   * l'application. Sans effet si elle est déjà sur l'écran d'invitation.
   */
  private readonly invitationResume = effect(() => {
    const uid = this.auth.currentUser()?.uid ?? null;
    if (!uid || this.invitationResumedFor === uid) return;
    this.invitationResumedFor = uid;
    void this.invitation.resumePending();
  });

  /**
   * Demande l'autorisation de notification dès qu'une session s'ouvre.
   *
   * On suit l'authentification plutôt que le démarrage de l'application : au
   * tout premier lancement personne n'est connecté, et un appel unique au
   * démarrage ne demanderait jamais rien. Ici, l'autorisation est demandée au
   * lancement si la session existe déjà, sinon juste après la connexion.
   */
  private readonly pushRegistration = effect(() => {
    const uid = this.auth.currentUser()?.uid ?? null;
    if (!uid) {
      this.pushRequestedFor = null;
      return;
    }
    // Une seule demande par session : relancer à chaque émission du signal
    // rouvrirait la boîte de dialogue système sans raison.
    if (this.pushRequestedFor === uid) return;
    this.pushRequestedFor = uid;
    void this.notificationService.registerFcmToken();
  });

  ngOnInit(): void {
    // Première navigation aboutie : les gardes ont rendu leur verdict et le
    // chunk de la route est chargé. `take(1)` se désabonne de lui-même.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => markStartup(StartupMark.RouteActivated));

    // Relevé des flux à chaque navigation : c'est en passant d'un écran à
    // l'autre que d'éventuels doublons de listeners se révèlent.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => setTimeout(() => reportFirestore((event as NavigationEnd).url), 2500));

    // OTA en premier : initialize() émet notifyAppReady() au plus tôt pour désarmer le
    // watchdog anti-crash natif avant tout travail d'init plus lourd.
    void this.otaUpdateService.initialize();
    // Theming white-label : applique le thème du tenant (no-op total en mono-marque).
    void this.themeService.applyTenantTheme();
    this.deepLinkService.init();
    // Premier lancement après une installation venue d'un lien partagé : le
    // navigateur qui l'a ouvert n'a rien pu transmettre à l'application, donc on
    // redemande l'invitation au serveur plutôt que d'obliger à retrouver le
    // message et à recliquer. Sans effet les fois suivantes.
    void this.invitation.resumePending();
    this.networkService.init();
    // Reprend les saisies mises en file lors d'une session hors ligne précédente.
    void this.outbox.init();
    this.keyboardService.init();

    // Notification reçue application ouverte : on la montre en bandeau plutôt
    // que dans le centre de notifications, que le système n'alimente pas au
    // premier plan.
    void this.notificationService.listenToForegroundMessages((payload) => {
      const text = payload.body ?? payload.title;
      if (text) this.toast.info(text);
    });

    // Notification touchée : ouvrir l'événement concerné, pas l'accueil.
    void this.notificationService.listenToNotificationTaps((data) => {
      const eventId = typeof data['eventId'] === 'string' ? data['eventId'] : '';
      if (eventId) void this.router.navigate(['/events', eventId]);
    });

    // Android hardware back button
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          CapacitorApp.exitApp();
        }
      });
    }
  }
}
